import { LuChevronDown } from "react-icons/lu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export type SelectFilterOption<T> = {
  key: string;
  label: string;
  value: T;
  icon?: React.ComponentType<{ className?: string }> | null;
};

export type SelectFilterDropdownProps<T> = {
  options: SelectFilterOption<T>[];
  selected: T;
  onChange: (value: T) => void;
  className?: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
};

function SelectFilterDropdown<T>({
  options,
  selected,
  onChange,
  className,
  disabled,
  label,
  placeholder = "Select",
}: SelectFilterDropdownProps<T>) {
  const selectedOption = options.find((item) => item.value === selected);

  const handleChange = (value: T) => {
    // Only call onChange if the value is actually different
    if (value !== selected) {
      onChange(value);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={disabled}
        className={cn(
          "max-w-[200px] flex h-9 items-center justify-between whitespace-nowrap rounded-[8px] border-[1px] border-solid border-border-gray bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-0",
          className,
        )}
      >
        <div className="mr-2 w-full flex items-center gap-2 overflow-hidden">
          {label && (
            <span className="shrink-0 overflow-hidden text-brand-primary-400 text-ellipsis text-[11px] font-normal leading-none tracking-[0.24px]">
              {label}
            </span>
          )}
          <span className="flex-1 line-clamp-1 text-brand-primary-800 text-[11px] font-medium leading-none tracking-[0.24px] capitalize">
            {selectedOption?.label || placeholder}
          </span>
        </div>
        {!disabled && (
          <LuChevronDown className="h-[14px] w-[14px] text-brand-primary-400" />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-[8px]">
        <DropdownMenuGroup>
          {options.map((item) => (
            <DropdownMenuItem
              key={item.key}
              onClick={() => handleChange(item.value)}
              className="cursor-pointer w-full"
            >
              <div className="w-full flex items-center gap-2">
                {item.icon && (
                  <item.icon className="h-[16px] w-[16px] text-brand-orange shrink-0" />
                )}
                <span className="text-brand-primary-new text-[13px] font-normal leading-[20px] capitalize">
                  {item.label}
                </span>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default SelectFilterDropdown;
